/** EMPLOYEE ONBOARDING — Add employee → Select package → Confirm. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, UserPlus, X } from "lucide-react";
import { Field, PackageCard, KV, btnP, btnS, inp } from "./onboardingParts";

const BLANK = { name: "", employee_code: "", department: "", designation: "", manager_email: "", email: "", phone: "", joining_date: "", location_id: "", work_location: "", delivery_address: "", country: "India", package_id: "", notes: "" };
const FIELDS = [
  ["name", "Employee Name", true], ["employee_code", "Employee ID"], ["department", "Department"], ["designation", "Designation"], ["manager_email", "Manager (email)"],
  ["email", "Email", false, "email"], ["phone", "Mobile"], ["joining_date", "Joining Date", false, "date"], ["country", "Country"],
];

export default function OnboardingWizard({ q, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState(BLANK);
  const [locs, setLocs] = useState([]);
  const [pkgs, setPkgs] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  useEffect(() => { api.get(`/portal/locations${q}`).then(({ data }) => setLocs(data.items || data || [])).catch(() => {}); }, [q]);
  useEffect(() => { if (step === 2 && !pkgs) api.get(`/onboarding/packages${q}`).then(({ data }) => setPkgs(data.items)).catch(() => toast.error("Could not load packages")); }, [step, pkgs, q]);
  const pkg = pkgs?.find((p) => p.id === f.package_id);
  const mc = new URLSearchParams(q).get("master_company_id");

  const submit = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      if (mc) body.master_company_id = mc;
      const { data } = await api.post("/onboarding", body);
      toast.success(`Onboarding started for ${data.employee_name}`);
      onCreated(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start onboarding"); }
    finally { setBusy(false); }
  };

  return (
    <div className="os-card p-5 space-y-4 os-fade-in" data-testid="onb-wizard">
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-lg grid place-items-center bg-[color:var(--st-accent-soft)] text-[color:var(--st-accent)]"><UserPlus className="h-4 w-4" /></span>
        <div className="flex-1"><div className="text-[15px] font-semibold">New employee onboarding</div><div className="text-[11px] text-[color:var(--st-text-muted)]">Step {step} of 3 · {["Add employee", "Select equipment package", "Confirm"][step - 1]}</div></div>
        <div className="hidden sm:flex items-center gap-1">{[1, 2, 3].map((n) => <span key={n} className={`h-1.5 w-8 rounded-full ${n <= step ? "bg-[color:var(--st-accent)]" : "bg-[color:var(--st-border)]"}`} />)}</div>
        <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]" data-testid="onb-wizard-close"><X className="h-4 w-4" /></button>
      </div>

      {step === 1 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="onb-step-employee">
          {FIELDS.map(([k, label, req, type]) => <Field key={k} label={label} required={req}><input type={type || "text"} value={f[k]} onChange={set(k)} className={inp} data-testid={`onb-f-${k}`} /></Field>)}
          <Field label="Work Location">
            {locs.length ? <select value={f.location_id} onChange={set("location_id")} className={inp} data-testid="onb-f-location_id"><option value="">Select location…</option>{locs.map((l) => <option key={l.id} value={l.id}>{l.name}{l.city ? ` · ${l.city}` : ""}</option>)}</select>
              : <input value={f.work_location} onChange={set("work_location")} placeholder="Office / city" className={inp} data-testid="onb-f-work_location" />}
          </Field>
          <div className="sm:col-span-2"><Field label="Delivery Address" required><textarea rows={3} value={f.delivery_address} onChange={set("delivery_address")} className={inp + " h-auto py-2"} data-testid="onb-f-delivery_address" /></Field></div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="onb-step-package">
          {!pkgs ? <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{[0, 1, 2].map((i) => <div key={i} className="os-skeleton h-56 rounded-xl" />)}</div>
            : <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{pkgs.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} selected={f.package_id === p.id} onSelect={() => setF((x) => ({ ...x, package_id: p.id }))} />)}</div>}
        </div>
      )}

      {step === 3 && pkg && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="onb-step-confirm">
          <div className="space-y-1"><div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)] font-semibold">Employee</div>
            {FIELDS.map(([k, label]) => <KV key={k} k={label} v={f[k]} />)}<KV k="Work Location" v={locs.find((l) => l.id === f.location_id)?.name || f.work_location} /><KV k="Delivery Address" v={f.delivery_address} /></div>
          <div className="space-y-1"><div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-text-subtle)] font-semibold">Equipment package · system plan</div>
            <KV k="Package" v={pkg.name} testId="onb-confirm-package" /><KV k="Available stock" v={`${pkg.plan.available_stock} of ${pkg.plan.required_units} unit(s)`} /><KV k="Warehouse" v={pkg.plan.primary_warehouse || "—"} />
            <KV k="Procurement required" v={pkg.plan.procurement_required ? `${pkg.plan.procurement_required} unit(s)` : "No"} testId="onb-confirm-procurement" /><KV k="Expected delivery" v={pkg.plan.expected_delivery} testId="onb-confirm-eta" />
            <KV k="Configuration" v={pkg.plan.configuration.join(" · ")} />
            <Field label="Notes"><textarea rows={2} value={f.notes} onChange={set("notes")} className={inp + " h-auto py-2"} data-testid="onb-f-notes" /></Field></div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        {step > 1 && <button onClick={() => setStep(step - 1)} className={btnS} data-testid="onb-back"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>}
        <span className="flex-1" />
        {step < 3 && <button onClick={() => setStep(step + 1)} disabled={(step === 1 && (!f.name.trim() || !f.delivery_address.trim())) || (step === 2 && !f.package_id)} className={btnP} data-testid="onb-next">{step === 1 ? "Select package" : "Review"} <ArrowRight className="h-3.5 w-3.5" /></button>}
        {step === 3 && <button onClick={submit} disabled={busy} className={btnP} data-testid="onb-submit">{busy ? "Starting…" : "Start onboarding"}</button>}
      </div>
    </div>
  );
}
